import { useEffect, useState } from 'react';
import { useTheme } from '../../hooks/useTheme.jsx';
import { useSchool } from '../../contexts/SchoolContext';
import { useBranding } from './BrandingProvider';

const DEFAULT_SWAN_LOGO = '/swan-wan-logo.jpg';
const HIGH_PRIORITY_IMAGE_PROPS = { fetchpriority: 'high' };

function SwanWordmarkFallback({ className = '' }) {
  return (
    <span
      role="img"
      aria-label="Swan Schools"
      className={`h-12 inline-flex items-center font-black tracking-[-0.06em] text-3xl leading-none ${className}`}
    >
      <span className="text-primary">S</span>
      <span className="text-base-content">WAN</span>
    </span>
  );
}

/**
 * The app's prime-placement mark. When a school has white-label branding active
 * (Pro entitlement + switched on + a brand colour), this shows the school's
 * badge and name and Swan steps back. Otherwise it's the Swan wordmark.
 *
 * `isBranded` comes from ThemeProvider and already reflects the fully-gated
 * active state (SchoolBrandingController only pushes a theme when entitled),
 * so we don't re-run the entitlement check here.
 */
export default function BrandLogo({ className = '' }) {
  const { isBranded } = useTheme();
  const { school } = useSchool();
  const { logoUrl } = useBranding();
  const name = school?.name;
  const [defaultLogoFailed, setDefaultLogoFailed] = useState(false);
  const [schoolLogoFailed, setSchoolLogoFailed] = useState(false);

  useEffect(() => {
    setSchoolLogoFailed(false);
  }, [logoUrl]);

  if (!isBranded) {
    if (defaultLogoFailed) {
      return <SwanWordmarkFallback className={className} />;
    }

    return (
      <img
        src={DEFAULT_SWAN_LOGO}
        alt="Swan Schools"
        className={`h-12 w-auto object-contain ${className}`}
        decoding="async"
        {...HIGH_PRIORITY_IMAGE_PROPS}
        onError={() => setDefaultLogoFailed(true)}
      />
    );
  }

  return (
    <span className={`flex items-center gap-2.5 ${className}`}>
      {logoUrl && !schoolLogoFailed ? (
        <img
          src={logoUrl}
          alt={name || 'School'}
          className="h-10 w-10 rounded-lg object-contain bg-base-200"
          decoding="async"
          {...HIGH_PRIORITY_IMAGE_PROPS}
          onError={() => setSchoolLogoFailed(true)}
        />
      ) : (
        <span className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center text-primary-content text-lg font-bold shrink-0">
          {(name || 'S').charAt(0)}
        </span>
      )}
      <span className="font-bold text-lg text-base-content truncate max-w-[10rem] sm:max-w-[16rem]">
        {name || 'Your School'}
      </span>
    </span>
  );
}
